import { useEffect, useEffectEvent, useRef } from 'react';
import {
  subscribeAppStateChange,
  subscribeVisibilityChange,
} from 'src/modules/appLifecycle';
import axios from 'axios';
import { useStartAppSession, sendAppSessionStamp } from 'src/queries/app_sessions';
import { useUser } from 'src/contexts/UserContext';
import { getCurrentUser, refreshIdTokenCache } from 'src/modules/firebase';
import { reportError } from 'src/modules/logs/Sentry';
import { isChildRole } from 'src/utils/userRole';
import { readStoredItem, writeStoredItem } from 'src/utils/storageManager';

// The backend applies the same tolerance on open (get-or-create), so these must move together.
export const HEARTBEAT_MS = 3 * 60 * 1000;
export const RESUME_TOLERANCE_MS = 5 * 60 * 1000;

// A stamp this long after the last known-alive time cannot be a late timer: the server extends
// the row by up to the tolerance per stamp, so stamping would absorb the frozen gap into it.
const LATE_STAMP_LIMIT_MS = HEARTBEAT_MS + RESUME_TOLERANCE_MS;

// Two lifecycle events for one background (Capacitor appStateChange + visibilitychange) are one stamp.
const STAMP_DEDUPE_MS = 1000;

type StoredSession = { sessionId: number; lastStampAt: number; stamped: boolean };

export const appSessionStorageKey = (userId: number) => `app_session:${userId}`;

export const readStoredSession = (userId: number): StoredSession | null => {
  try {
    const raw = readStoredItem(appSessionStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.sessionId !== 'number' || typeof parsed.lastStampAt !== 'number') return null;
    return { sessionId: parsed.sessionId, lastStampAt: parsed.lastStampAt, stamped: parsed.stamped === true };
  } catch {
    return null;
  }
};

const writeStoredSession = (userId: number, value: StoredSession) => {
  writeStoredItem(appSessionStorageKey(userId), JSON.stringify(value));
};

// A 401 means the request raced an auth teardown, not a real auth failure.
const reportSessionError = (message: string, error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 401) return;
  reportError(message, error);
};

export const AppSessionTracking = () => {
  const { me: user } = useUser();
  const sessionIdRef = useRef<number | null>(null);
  const lastStampAtRef = useRef<number | null>(null);
  const lastSentAtRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Effect-run generation: bumped on every effect start and cleanup, so an open that resolves after a
  // child switch or unmount can tell it is stale and must not touch refs, storage or the timer.
  const generationRef = useRef(0);
  const openingGenerationRef = useRef<number | null>(null);

  const startSessionMutation = useStartAppSession();

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // sendBeacon can refuse (false) or, rarely, throw (security error): both mean "not sent".
  const trySend = (sessionId: number, at: number): boolean => {
    try {
      const sent = sendAppSessionStamp(sessionId, new Date(at));
      if (!sent) console.warn('App session stamp not sent');
      return sent;
    } catch (error) {
      console.warn('App session stamp failed', error);
      return false;
    }
  };

  // Synchronous on purpose: a lifecycle event is the last tick the WebView may get.
  // `reopenGeneration` is set only by the heartbeat (app in use): a lifecycle stamp past the late
  // limit just drops the session, so a background event never opens a phantom row.
  const stamp = (userId: number, reopenGeneration: number | null, at = Date.now()) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    const lastAlive = lastStampAtRef.current;
    if (lastAlive !== null && at - lastAlive > LATE_STAMP_LIMIT_MS) {
      // Treat it as a return beyond tolerance: the old row keeps its last real stamp.
      sessionIdRef.current = null;
      lastStampAtRef.current = null;
      clearTimer();
      if (reopenGeneration !== null && generationRef.current === reopenGeneration) {
        void openSession(userId, reopenGeneration);
      }
      return;
    }
    if (lastSentAtRef.current !== null && at - lastSentAtRef.current < STAMP_DEDUPE_MS) return;
    if (!trySend(sessionId, at)) return;
    lastSentAtRef.current = at;
    lastStampAtRef.current = at;
    writeStoredSession(userId, { sessionId, lastStampAt: at, stamped: true });
  };

  const armHeartbeat = (userId: number, generation: number) => {
    clearTimer();
    timerRef.current = setTimeout(() => {
      stamp(userId, generation);
      if (!sessionIdRef.current) return;
      void refreshIdTokenCache().catch(() => undefined);
      armHeartbeat(userId, generation);
    }, HEARTBEAT_MS);
  };

  const openSession = async (userId: number, generation: number) => {
    const isStale = () => generationRef.current !== generation;
    // In-flight dedupe is scoped per effect run, so it never blocks the next child's open.
    if (openingGenerationRef.current === generation) return;
    openingGenerationRef.current = generation;
    try {
      const firebaseUser = await getCurrentUser();
      if (!firebaseUser || isStale()) return;
      await refreshIdTokenCache();
      if (isStale()) return;

      const stored = readStoredSession(userId);
      const now = Date.now();
      if (stored && now - stored.lastStampAt <= RESUME_TOLERANCE_MS) {
        sessionIdRef.current = stored.sessionId;
        lastStampAtRef.current = stored.lastStampAt;
        armHeartbeat(userId, generation);
        return;
      }
      if (stored?.stamped) {
        // Catch-up: the background stamp before a kill may never have left the device. An entry that
        // only holds the open time has nothing to catch up (the server answers 400, end = start).
        trySend(stored.sessionId, stored.lastStampAt);
      }

      const session = await startSessionMutation.mutateAsync();
      if (isStale()) return;
      sessionIdRef.current = session.id;
      lastStampAtRef.current = now;
      writeStoredSession(userId, { sessionId: session.id, lastStampAt: now, stamped: false });
      armHeartbeat(userId, generation);
    } catch (error) {
      reportSessionError('Failed to start app session:', error);
    } finally {
      if (openingGenerationRef.current === generation) openingGenerationRef.current = null;
    }
  };

  const userId = user?.id;
  const userType = user?.user_type;

  const onOpenSession = useEffectEvent(openSession);
  const onArmHeartbeat = useEffectEvent(armHeartbeat);
  const onStamp = useEffectEvent(stamp);
  const onClearTimer = useEffectEvent(clearTimer);

  useEffect(() => {
    if (!userId || !isChildRole(userType)) return;
    const trackedUserId = userId;

    const generation = ++generationRef.current;

    void onOpenSession(trackedUserId, generation);

    const unsubscribeAppState = subscribeAppStateChange(isActive => {
      if (generationRef.current !== generation) return;
      if (isActive) {
        const last = lastStampAtRef.current;
        if (sessionIdRef.current && last !== null && Date.now() - last <= RESUME_TOLERANCE_MS) {
          onArmHeartbeat(trackedUserId, generation);
          // The cached token must outlive the next heartbeat gap.
          void refreshIdTokenCache().catch(() => undefined);
        } else {
          // Drop the old row first so a background event during the reopen cannot stamp it.
          sessionIdRef.current = null;
          lastStampAtRef.current = null;
          void onOpenSession(trackedUserId, generation);
        }
      } else {
        onClearTimer();
        onStamp(trackedUserId, null);
      }
    });

    const unsubscribeVisibility = subscribeVisibilityChange(visibilityState => {
      if (generationRef.current !== generation) return;
      if (visibilityState === 'hidden') {
        onClearTimer();
        onStamp(trackedUserId, null);
      }
    });

    return () => {
      generationRef.current = generation + 1;
      onClearTimer();
      onStamp(trackedUserId, null);
      sessionIdRef.current = null;
      lastStampAtRef.current = null;
      lastSentAtRef.current = null;
      unsubscribeVisibility();
      unsubscribeAppState();
    };
  }, [userId, userType]);

  return null;
};
